import React, {useEffect, useMemo, useRef, useState} from 'react';
import Icon from '@/shared/components/Icon';
import type {Recommendation} from '@/shared/types/api';
import ProductCard from '@/modules/patient-dashboard/components/ProductCard';
import SectionContainer from '@/shared/components/SectionContainer';
import DashboardButton from '@/shared/components/DashboardButton';
import myLabsRx from '@/assets/dashboard/my_labs_rx.png';
import BioMatchCarouselCard from './BioMatchCarouselCard';
import BloodViabilityCountdown from './BloodViabilityCountdown';
import BioMatchEnrollmentService from '../services/bioMatchEnrollmentService';

/**
 * Patient-side renderer for the B2B BioMatch surface (developer spec
 * sections 2.4 + 5.3). Layout matches the legacy `Recommendations.tsx`:
 *
 *   1. Critical referrals (recommendation_type='referral') stack as
 *      red full-width banners at the top — these are urgent medical
 *      alerts, not products.
 *   2. Programs, care_pathways, lifestyle, and supplements all render
 *      inside a single horizontal scrolling carousel of uniform 263px
 *      tiles, with left/right nav buttons that appear when the row
 *      overflows. The "My Labs Rx" upsell is pinned as the first tile
 *      on the dashboard variant when the patient isn't subscribed.
 *
 * Engine has already sorted by risk_tier → priority_weight → id and
 * suppressed LOW tier when any HIGH/CRITICAL is present. The component
 * trusts that order.
 */

interface Props {
    recommendations: Recommendation[];
    /**
     * 'dashboard' adds the "My Labs Rx" upsell card to the carousel
     * when the patient isn't subscribed; 'results' omits it because
     * the lab-results page is post-purchase context.
     */
    variant?: 'dashboard' | 'results';
    isSubscribed?: boolean;
    loading?: boolean;
    /**
     * Compact mode used by the dashboard summary tile — renders one
     * tier-colored chip per recommendation with no CTA.
     */
    compact?: boolean;
    maxVisible?: number;
    onViewAll?: () => void;
    className?: string;
}

const myLabsRxCard = {
    name: 'My Labs Rx',
    category: 'You Qualify',
    description:
        'Unlock in-depth lab features and a quarterly lab kit curated for your unique health needs and goals.',
    price: '$99.00/mo',
    image: myLabsRx,
};

const stripHtml = (html: string | null | undefined): string => {
    if (!html) return '';
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
};

const TIER_CHIP_CLASSES: Record<string, string> = {
    critical: 'bg-rose-100 text-rose-800 border-rose-200',
    high: 'bg-orange-100 text-orange-800 border-orange-200',
    moderate: 'bg-amber-100 text-amber-800 border-amber-200',
    low: 'bg-mld-primary/10 text-mld-primary border-mld-primary/20',
};

function bucketByType(recs: Recommendation[]) {
    const buckets = {
        referral: [] as Recommendation[],
        // Everything except referrals lands in the carousel; engine
        // sort order is preserved by appending in iteration order.
        carousel: [] as Recommendation[],
    };
    for (const r of recs) {
        if (r.recommendation_type === 'referral') {
            buckets.referral.push(r);
        } else {
            buckets.carousel.push(r);
        }
    }
    return buckets;
}

// ── Critical referral banner ──────────────────────────────────────────────
const CriticalReferralBanner: React.FC<{rec: Recommendation}> = ({rec}) => {
    // Critical alerts are the only impression source for the spec's
    // "Critical alert count" employer metric — record on render.
    useEffect(() => {
        BioMatchEnrollmentService.recordImpression(rec.id, 'view');
    }, [rec.id]);
    return (
    <div className="rounded-lg border-2 border-rose-300 bg-rose-50 p-4 shadow-sm">
        <div className="flex items-start gap-3">
            <div className="flex-shrink-0">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-rose-200">
                    <Icon name="circle-exclamation" className="h-6 w-6 text-rose-700" />
                </div>
            </div>
            <div className="flex-1 min-w-0">
                <h3 className="text-base font-semibold text-rose-900">{rec.name}</h3>
                {rec.cta_body_text && (
                    <p className="mt-1 text-sm leading-relaxed text-rose-800">{rec.cta_body_text}</p>
                )}
                {!rec.cta_body_text && rec.content && (
                    <p className="mt-1 text-sm leading-relaxed text-rose-800">{stripHtml(rec.content)}</p>
                )}
                {rec.cta_button_label && (
                    <p className="mt-3 text-sm font-medium text-rose-900">{rec.cta_button_label}</p>
                )}
            </div>
        </div>
    </div>
    );
};

// ── Compact summary chips (dashboard tile) ────────────────────────────────
const CompactChips: React.FC<{
    recs: Recommendation[];
    maxVisible: number;
    onViewAll?: () => void;
}> = ({recs, maxVisible, onViewAll}) => {
    const visible = recs.slice(0, maxVisible);
    const hasMore = recs.length > maxVisible;
    return (
        <div className="flex flex-wrap gap-1">
            {visible.map((rec) => {
                const tierClass = rec.risk_tier ? TIER_CHIP_CLASSES[rec.risk_tier] ?? '' : '';
                return (
                    <span
                        key={rec.id}
                        className={`inline-flex items-center text-xs px-2 py-1 rounded-md border ${
                            tierClass || 'bg-amber-100 text-amber-800 border-amber-200'
                        }`}
                        title={rec.cta_body_text || rec.content || ''}>
                        <Icon name="circle-exclamation" className="h-3 w-3 mr-1" />
                        {rec.name}
                    </span>
                );
            })}
            {hasMore && (
                <button
                    onClick={onViewAll}
                    className="inline-flex items-center text-xs text-mld-text-secondary hover:text-mld-text-primary px-2 py-1 rounded-md hover:bg-mld-panel-bg transition-colors">
                    +{recs.length - maxVisible} more
                </button>
            )}
        </div>
    );
};

// ── Horizontal scrolling carousel ─────────────────────────────────────────
const Carousel: React.FC<{
    recs: Recommendation[];
    isSubscribed: boolean;
    showMyLabsRx: boolean;
}> = ({recs, isSubscribed, showMyLabsRx}) => {
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [showScrollButtons, setShowScrollButtons] = useState(false);

    const showUpsell = showMyLabsRx && !isSubscribed;
    const hasCards = showUpsell || recs.length > 0;

    useEffect(() => {
        if (!hasCards) {
            setShowScrollButtons(false);
            return;
        }
        const checkScrollNeeded = () => {
            if (scrollContainerRef.current) {
                const {scrollWidth, clientWidth} = scrollContainerRef.current;
                setShowScrollButtons(scrollWidth > clientWidth);
            }
        };
        checkScrollNeeded();
        window.addEventListener('resize', checkScrollNeeded);
        return () => window.removeEventListener('resize', checkScrollNeeded);
    }, [recs, hasCards]);

    if (!hasCards) return null;

    const scrollBy = (delta: number) => {
        scrollContainerRef.current?.scrollBy({left: delta, behavior: 'smooth'});
    };

    return (
        <div className="relative">
            {showScrollButtons && (
                <>
                    <button
                        onClick={() => scrollBy(-283)}
                        aria-label="Scroll left"
                        className="absolute left-[-18px] top-1/2 -translate-y-1/2 z-10 w-5 h-5 bg-mld-panel-bg rounded-full flex items-center justify-center text-mld-card-text hover:text-mld-primary transition-colors border border-mld-panel-border">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>
                    <button
                        onClick={() => scrollBy(283)}
                        aria-label="Scroll right"
                        className="absolute right-[-18px] top-1/2 -translate-y-1/2 z-10 w-5 h-5 bg-mld-panel-bg rounded-full flex items-center justify-center text-mld-card-text hover:text-mld-primary transition-colors border border-mld-panel-border">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                    </button>
                </>
            )}
            <div ref={scrollContainerRef} className="flex gap-4 overflow-x-auto pb-2 pl-6 pr-10 scrollbar-hide">
                {showUpsell && <ProductCard product={myLabsRxCard} />}
                {recs.map((rec) => (
                    <BioMatchCarouselCard key={rec.id} rec={rec} />
                ))}
            </div>
        </div>
    );
};

// ── Main surface ──────────────────────────────────────────────────────────
const BioMatchCardSurface: React.FC<Props> = ({
    recommendations,
    variant = 'dashboard',
    isSubscribed = false,
    loading = false,
    compact = false,
    maxVisible = 3,
    onViewAll,
    className = '',
}) => {
    const buckets = useMemo(() => bucketByType(recommendations || []), [recommendations]);

    if (compact) {
        if (!recommendations || recommendations.length === 0) return null;
        return (
            <div className={className}>
                <CompactChips recs={recommendations} maxVisible={maxVisible} onViewAll={onViewAll} />
            </div>
        );
    }

    if (loading) {
        if (variant === 'dashboard') {
            return (
                <SectionContainer title="🧬 BioMatch™: Personalized Recommendations, Powered by Your Blood">
                    <div className="text-center py-8 text-mld-card-text">Loading recommendations...</div>
                </SectionContainer>
            );
        }
        return null;
    }

    const hasAnyContent = buckets.referral.length > 0 || buckets.carousel.length > 0;

    if (variant === 'results') {
        if (!hasAnyContent) return null;
        return (
            <div className={`space-y-4 ${className}`}>
                {buckets.referral.map((rec) => (
                    <CriticalReferralBanner key={rec.id} rec={rec} />
                ))}
                {buckets.carousel.length > 0 && (
                    <Carousel recs={buckets.carousel} isSubscribed={isSubscribed} showMyLabsRx={false} />
                )}
            </div>
        );
    }

    // Dashboard variant. Critical banners pinned above the carousel so
    // the urgent stuff doesn't get scrolled past, then the unified
    // horizontal carousel for everything else.
    return (
        <SectionContainer
            title="🧬 BioMatch™: Personalized Recommendations, Powered by Your Blood"
            className={`mb-6 ${className}`}>
            <div className="space-y-4">
                <BloodViabilityCountdown />
                {buckets.referral.length > 0 && (
                    <div className="space-y-3 px-6">
                        {buckets.referral.map((rec) => (
                            <CriticalReferralBanner key={rec.id} rec={rec} />
                        ))}
                    </div>
                )}
                <Carousel recs={buckets.carousel} isSubscribed={isSubscribed} showMyLabsRx />
                <div className="flex justify-end mt-4">
                    <DashboardButton text="See More" />
                </div>
            </div>
        </SectionContainer>
    );
};

export default BioMatchCardSurface;
